// Roster — left dark filterable list + right white detail card with full operator profile.
// Source canonical: design-system/project/homepage/index.html § RosterSection.

import { useState } from 'react';
import Portrait from '../components/Portrait';
import Wordmark from '../components/Wordmark';
import { ROSTER, type Operator } from '../data/roster';

type Filter = 'all' | 'hiring-now' | 'interviewing' | 'planned';

const FILTERS: { k: Filter; l: string }[] = [
  { k: 'all',          l: 'All · 6' },
  { k: 'hiring-now',   l: 'Available · 3' },
  { k: 'interviewing', l: 'Interviewing · 2' },
  { k: 'planned',      l: 'In training · 1' },
];

const STATUS = {
  'hiring-now':   { fg: '#2F6B4D', bg: '#E0EFE6', label: 'AVAILABLE' },
  'interviewing': { fg: '#C06E47', bg: '#F5E8DF', label: 'INTERVIEWING' },
  'planned':      { fg: '#78716C', bg: '#F5F4F2', label: 'IN TRAINING' },
} as const;

function MEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: 11, fontWeight: 600,
      letterSpacing: '0.12em', textTransform: 'uppercase',
      color: '#D4835E',
    }}>{children}</span>
  );
}

export default function Roster() {
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<string>('priya');

  const filtered = filter === 'all' ? ROSTER : ROSTER.filter(x => x.status === filter);
  const a: Operator = ROSTER.find(x => x.id === selectedId) ?? ROSTER[0];

  return (
    <section
      id="roster"
      style={{
        padding: '96px 40px',
        background: 'linear-gradient(180deg, #1A1918 0%, #0E0E0C 100%)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Steel glow top-left */}
      <div style={{
        position: 'absolute', top: 0, left: -200, width: 700, height: 700, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(94,131,179,0.10) 0%, transparent 70%)',
      }}/>

      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <MEyebrow>The roster · hiring now</MEyebrow>
        <div style={{ marginTop: 14, marginBottom: 18 }}>
          <Wordmark size="md" name="Operators" />
        </div>
        <h2 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 48, fontWeight: 400,
          fontVariationSettings: "'opsz' 96",
          letterSpacing: '-0.02em', lineHeight: 1.1,
          color: '#F5F4F2',
          margin: '0 0 14px', maxWidth: 780,
        }}>
          Six applicants. Each one wants a job.
        </h2>
        <p style={{
          fontFamily: 'Inter Tight, system-ui, sans-serif',
          fontSize: 16, lineHeight: 1.6,
          color: '#C7C3BD',
          maxWidth: 680, margin: '0 0 36px',
        }}>
          Open any application — experience, stack, cost per shift, a sample day.
        </p>

        {/* Filter chips */}
        <div className="roster-filter-chips" style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          {FILTERS.map(({ k, l }) => {
            const on = filter === k;
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                style={{
                  padding: '7px 13px', borderRadius: 9999,
                  border: `1px solid ${on ? '#D4835E' : 'rgba(255,255,255,0.10)'}`,
                  background: on ? '#D4835E' : '#232120',
                  color: on ? '#1A1918' : '#C7C3BD',
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: 11, fontWeight: 600,
                  letterSpacing: '0.04em', cursor: 'pointer', textTransform: 'uppercase',
                  transition: 'all 150ms ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {l}
              </button>
            );
          })}
        </div>

        {/* DESKTOP: dual list + detail */}
        <div className="roster-grid lg-only" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20 }}>
          {/* LEFT — applicant list (dark) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(x => {
              const on = selectedId === x.id;
              return (
                <div
                  key={x.id}
                  onClick={() => setSelectedId(x.id)}
                  style={{
                    padding: '12px 14px',
                    background: on ? '#2C2A28' : '#232120',
                    border: `1px solid ${on ? 'rgba(212,131,94,0.40)' : 'rgba(255,255,255,0.06)'}`,
                    borderLeft: `3px solid ${on ? '#D4835E' : 'transparent'}`,
                    borderRadius: 10, cursor: 'pointer',
                    boxShadow: on ? '0 6px 18px -8px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
                    transition: 'all 150ms ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Portrait src={x.photo} name={x.name} size={44}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#F5F4F2', fontFamily: 'Fraunces, serif' }}>{x.name}</div>
                      <div style={{ fontSize: 11.5, color: '#908A82' }}>{x.role}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT — white detail card (the dramatic product-UI moment) */}
          <div className="roster-detail" style={{
            background: '#fff',
            border: '1px solid #E8E6E1',
            borderRadius: 14,
            padding: 32,
            borderTop: `4px solid ${a.color}`,
            color: '#1A1918',
          }}>
            <div className="roster-detail-header" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <Portrait src={a.photo} name={a.name} size={88}/>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 30, fontWeight: 500, color: '#1A1918', margin: 0, letterSpacing: '-0.01em' }}>{a.name}</h3>
                  <span style={{ fontSize: 14, color: '#57534E' }}>· {a.role}</span>
                </div>
                <div style={{ fontSize: 14, color: '#44403C', marginTop: 8, fontStyle: 'italic' }}>"{a.hook}"</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#57534E', background: '#F5F4F2', padding: '4px 8px', borderRadius: 4 }}>
                    <b style={{ color: '#1A1918' }}>REPLACES</b> &nbsp;{a.replaces}
                  </span>
                  <span style={{
                    fontFamily: 'IBM Plex Mono, monospace', fontSize: 10,
                    color: STATUS[a.status].fg, background: STATUS[a.status].bg,
                    padding: '4px 8px', borderRadius: 4,
                  }}>
                    <b>{STATUS[a.status].label}</b>
                  </span>
                </div>
              </div>
            </div>

            {/* 4-metric grid */}
            <div className="roster-metric-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginTop: 24, paddingTop: 20, borderTop: '1px solid #F5F4F2' }}>
              {([['Per run', a.cost], ['Speed', a.speed], ['Quality', a.quality], ['Runs', a.runs]] as const).map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#78716C', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{l}</div>
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 22, fontWeight: 600, color: '#1A1918', marginTop: 4 }}>{v}</div>
                </div>
              ))}
            </div>

            <div className="roster-twocol" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 24 }}>
              {/* Experience + languages + stack */}
              <div>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#57534E' }}>Experience</div>
                <div style={{ fontSize: 13, color: '#44403C', marginTop: 8 }}>{a.exp}</div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#57534E' }}>Languages</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {a.languages.map(l => (
                      <span key={l} style={{ fontSize: 11, color: '#44403C', background: '#F5F4F2', padding: '3px 8px', borderRadius: 4 }}>{l}</span>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#57534E' }}>Stack fluency</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {a.stack.map(s => (
                      <span key={s} style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: a.color, background: a.bg, padding: '3px 8px', borderRadius: 4 }}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Deliverables + sample day */}
              <div>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#57534E' }}>Deliverables</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {a.deliverables.map(d => (
                    <li key={d} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#44403C' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={a.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m5 12 5 5L20 7"/>
                      </svg>
                      {d}
                    </li>
                  ))}
                </ul>

                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#57534E' }}>A day on the job</div>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {a.day.map((d, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.45 }}>
                      <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: a.color, fontWeight: 600, width: 44, flexShrink: 0 }}>{d.t}</span>
                      <span style={{ color: '#44403C' }}>{d.e}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #F5F4F2', display: 'flex', gap: 10 }}>
              {a.status === 'hiring-now' ? (
                <>
                  <a href="#waitlist" style={{
                    padding: '11px 22px', background: '#D4835E', color: '#1A1918',
                    border: '1px solid #D4835E', borderRadius: 8, textDecoration: 'none',
                    fontSize: 14, fontWeight: 700, fontFamily: 'Inter Tight, system-ui, sans-serif',
                  }}>Hire {a.name.split(' ')[0]} →</a>
                  <a href="#" style={{
                    padding: '11px 22px', background: 'transparent', color: '#1A1918',
                    border: '1px solid #D6D3D1', borderRadius: 8, textDecoration: 'none',
                    fontSize: 14, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif',
                  }}>See sample output</a>
                </>
              ) : (
                <a href="#waitlist" style={{
                  padding: '11px 22px', background: '#1A1918', color: '#fff',
                  border: '1px solid #1A1918', borderRadius: 8, textDecoration: 'none',
                  fontSize: 14, fontWeight: 700, fontFamily: 'Inter Tight, system-ui, sans-serif',
                }}>Request interview · early access →</a>
              )}
            </div>
          </div>
        </div>

        {/* MOBILE: horizontal swipe carousel of all 6 operators */}
        <div className="sm-only">
          <div className="h-carousel">
            {filtered.map(op => {
              const status = STATUS[op.status];
              return (
                <div key={op.id} className="roster-mobile-card">
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14 }}>
                    <Portrait src={op.photo} name={op.name} size={56}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 600, color: '#1A1918', lineHeight: 1.1 }}>{op.name}</div>
                      <div style={{ fontSize: 12, color: '#57534E', marginTop: 2 }}>{op.role} · AI Role</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#44403C', fontStyle: 'italic', lineHeight: 1.5, marginBottom: 14 }}>"{op.hook}"</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, paddingTop: 12, paddingBottom: 14, borderTop: '1px solid #F5F4F2', borderBottom: '1px solid #F5F4F2', marginBottom: 14 }}>
                    {([['Per run', op.cost], ['Speed', op.speed], ['Quality', op.quality]] as const).map(([l, v]) => (
                      <div key={l}>
                        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#78716C', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{l}</div>
                        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 16, fontWeight: 600, color: '#1A1918', marginTop: 2 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                    {op.stack.slice(0, 3).map(s => (
                      <span key={s} style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: op.color, background: op.bg, padding: '3px 8px', borderRadius: 4 }}>{s}</span>
                    ))}
                  </div>
                  <span style={{
                    display: 'inline-block', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10,
                    color: status.fg, background: status.bg,
                    padding: '4px 8px', borderRadius: 4, marginBottom: 12,
                  }}>
                    <b>{status.label}</b>
                  </span>
                  <a href="#waitlist" style={{
                    display: 'block', textAlign: 'center',
                    padding: '12px 18px',
                    background: op.status === 'hiring-now' ? '#D4835E' : '#1A1918',
                    color: op.status === 'hiring-now' ? '#1A1918' : '#fff',
                    borderRadius: 8, textDecoration: 'none',
                    fontSize: 14, fontWeight: 700,
                    fontFamily: 'Inter Tight, system-ui, sans-serif',
                  }}>
                    {op.status === 'hiring-now' ? `Hire ${op.name.split(' ')[0]} →` : 'Request interview →'}
                  </a>
                </div>
              );
            })}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 8,
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 10, color: '#908A82',
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            Swipe through {filtered.length} {filtered.length === 1 ? 'role' : 'roles'}
          </div>
        </div>
      </div>
    </section>
  );
}
